/** The name behind a foreign key on the audit page (#422).
 *
 *  The backend writes a label under a ROLE: `tournament`, `red`, `member`.
 *  The payload names the COLUMN: `tournament_id`, `red_athlete_record_id`,
 *  `user_id`. Most roles are the column with its `_id`, `_record_id` or
 *  `_user_id` suffix removed. The rest are listed here, because the guess
 *  `red_athlete` matched nothing, and both corners of every verdict showed
 *  a bare UUID while the event carried the names.
 *
 *  `audit-label-lookup.spec.ts` scans the backend for every role it writes
 *  and fails when one is reachable from no column.
 *
 *  A separate file, not `pages/Admin.tsx`: a Playwright spec imports it, and
 *  the page reaches `import.meta.env`, which the test transform cannot load.
 */
export const ROLE_BY_COLUMN: Readonly<Record<string, readonly string[]>> = {
  red_athlete_record_id: ['red'],
  blue_athlete_record_id: ['blue'],
  // `user_id` names a member on a team row, a judge on a bout_referees row
  // and an athlete elsewhere. One event carries one of them.
  user_id: ['member', 'judge', 'athlete'],
  requesting_user_id: ['claimant'],
  // #556 — the proposer of an End Weigh-in (weighin_ended, weighin_end_cancelled,
  // weighin_proposal_dropped).
  proposed_by: ['proposer'],
};

/** The roles a payload column may be labelled under, most specific first. */
export function candidateRoles(key: string): string[] {
  const stripped = key.replace(/_(record|user)?_?id$/, '');
  return [...new Set([key, stripped, ...(ROLE_BY_COLUMN[key] ?? [])])];
}

/** The label for one payload column, or null when the event has no name
 *  for it. The raw UUID then stands alone, as it did before #389. */
export function labelFor(key: string, labels?: Record<string, string> | null): string | null {
  if (!labels) return null;
  for (const role of candidateRoles(key)) {
    const v = labels[role];
    if (v) return v;
  }
  return null;
}
